import type { Joke } from '../types'

type CreateJokePayload = {
  content: string
  author?: string | null
}

type UpdateJokePayload = {
  content?: string
  author?: string | null
}

// Use /api prefix for BFF proxy, or fallback to direct URL for local development
const baseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '/api'

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Request failed with status ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

export async function getRandomJoke(): Promise<Joke> {
  const response = await fetch(`${baseUrl}/jokes/random`)
  return handleResponse<Joke>(response)
}

export async function getJokes(skip = 0, take = 100): Promise<Joke[]> {
  const params = new URLSearchParams({ skip: skip.toString(), take: take.toString() })
  const response = await fetch(`${baseUrl}/jokes?${params.toString()}`)
  return handleResponse<Joke[]>(response)
}

export async function createJoke(payload: CreateJokePayload): Promise<Joke> {
  const response = await fetch(`${baseUrl}/jokes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  return handleResponse<Joke>(response)
}

export async function updateJoke(id: string, payload: UpdateJokePayload): Promise<Joke> {
  const response = await fetch(`${baseUrl}/jokes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  return handleResponse<Joke>(response)
}

export async function deleteJoke(id: string): Promise<void> {
  const response = await fetch(`${baseUrl}/jokes/${id}`, { method: 'DELETE' })
  await handleResponse<void>(response)
}
